import * as Sentry from '@sentry/react';

import { buildCrashLocationMap } from './crashLocation';
import { buildIssueHeading, toSentryException } from './heading';
import {
  isPathLockedModule,
  moduleFromPathname,
  resolveBoundaryModule,
} from './moduleMapping';
import { owningTeamForModule } from './ownership';
import { incidentPriorityToLegacyTag } from './priority';
import { resolveSentryContext } from './resolve';
import { BOUNDARY_ERROR_CONFIG } from './constant';
import type {
  ResolvedSentryContext,
  SentryCaptureOptions,
  SentryLegacyCaptureInput,
} from './types';

const transactionNameFromPath = (pathname?: string): string => {
  if (pathname?.trim()) return pathname;
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return window.location.pathname;
  }
  return 'Dashboard';
};

let lastAnalyticsEvent: string | null = null;

export const recordLastAnalyticsEvent = (eventName: string): void => {
  lastAnalyticsEvent = eventName;
};

export const getLastAnalyticsEvent = (): string | null => lastAnalyticsEvent;

export const buildSentryTags = (
  context: ResolvedSentryContext,
  pathname?: string,
): Record<string, string> => {
  const tags: Record<string, string> = {
    app: 'dashboard',
    product: 'pg',
    repo_url: BOUNDARY_ERROR_CONFIG.repo_url,
    incident_priority: context.incident_priority,
  };

  if (context.module) tags.module = context.module;
  if (context.feature) tags.feature = context.feature;
  if (context.owning_team) tags.owning_team = context.owning_team;
  if (context.flow_tier) tags.flow_tier = context.flow_tier;
  if (context.matched_event) tags.matched_event = context.matched_event;
  if (pathname) tags.route = pathname;

  const legacy = incidentPriorityToLegacyTag(context.incident_priority);
  if (legacy) tags.priority = legacy;

  return tags;
};

export const applySentryContext = (
  scope: Sentry.Scope,
  context: ResolvedSentryContext,
  options?: Pick<SentryCaptureOptions, 'pathname' | 'extraTags' | 'extra'>,
): void => {
  const tags = {
    ...buildSentryTags(context, options?.pathname),
    ...options?.extraTags,
  };

  Object.entries(tags).forEach(([key, value]) => {
    if (value != null && value !== '') scope.setTag(key, value);
  });

  if (options?.extra) {
    Object.entries(options.extra).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
  }
};

export const captureSentryError = (
  error: unknown,
  options: SentryCaptureOptions = {},
): void => {
  const pathname =
    options.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined);

  const analyticsEvent = options.analyticsEvent ?? getLastAnalyticsEvent();
  const resolved = resolveSentryContext(analyticsEvent, pathname);
  const pathModule = moduleFromPathname(pathname);

  const module =
    isPathLockedModule(pathModule)
      ? pathModule
      : resolveBoundaryModule(
          options.module,
          options.componentName,
          pathname,
        ) ??
        resolved.module ??
        pathModule;
  const context: ResolvedSentryContext = {
    ...resolved,
    module,
    feature: options.feature ?? resolved.feature,
    owning_team: module
      ? owningTeamForModule(module)
      : resolved.owning_team,
  };

  const location = buildCrashLocationMap({
    pathname,
    module: context.module,
    componentName: options.componentName,
    feature: context.feature,
    owningTeam: context.owning_team,
    boundaryLevel: options.extraTags?.boundary_level,
    crashType: options.extraTags?.crash_type,
    crashOrigin: options.crash_origin,
    matchedEvent: context.matched_event ?? analyticsEvent ?? undefined,
    incidentPriority: context.incident_priority,
    apiEndpoint: options.apiEndpoint ?? options.extraTags?.fetchUrl,
    label: options.label,
  });

  const heading = buildIssueHeading(error, {
    module: context.module,
    componentName: options.componentName,
    crashType: options.extraTags?.crash_type,
    crashOrigin: options.crash_origin,
    feature: context.feature,
    label: options.label,
    apiEndpoint: options.apiEndpoint ?? options.extraTags?.fetchUrl,
    pathname,
  });

  const displayError = toSentryException(error, heading);
  const originalMessage =
    error instanceof Error ? error.message : String(error ?? '');

  Sentry.withScope(scope => {
    applySentryContext(scope, context, {
      pathname,
      extraTags: {
        issue_heading: heading,
        crash_map: location.map,
        ...(options.crash_origin ? { crash_origin: options.crash_origin } : {}),
        ...(options.label ? { label: options.label } : {}),
        ...options.extraTags,
      },
      extra: {
        original_message: originalMessage,
        crash_summary: location.summary,
        crash_map: location.map,
        ...options.extra,
      },
    });

    scope.setContext('Crash location', location.context);
    scope.setTransactionName(transactionNameFromPath(pathname));
    if (options.userId) {
      scope.setUser({ id: options.userId });
    }
    scope.setFingerprint([
      'dashboard',
      context.module ?? options.componentName ?? 'unknown',
      options.crash_origin ??
        options.extraTags?.crash_type ??
        options.label ??
        'error',
      displayError.name,
      displayError.message.slice(0, 120),
    ]);
    Sentry.captureException(displayError);
  });
};

export const sentryCaptureException = ({
  errMsg,
  fetchUrl,
  priority,
  label,
  action,
  statusCode,
  pathname,
  errorType,
  ...rest
}: SentryLegacyCaptureInput): void => {
  captureSentryError(errMsg, {
    pathname:
      pathname ??
      (typeof window !== 'undefined' ? window.location.pathname : undefined),
    crash_origin: errorType === 'api' ? 'api' : undefined,
    label,
    apiEndpoint: fetchUrl,
    extraTags: {
      ...(fetchUrl != null ? { fetchUrl: String(fetchUrl) } : {}),
      ...(priority != null ? { priority: String(priority) } : {}),
      ...(statusCode != null ? { statusCode: String(statusCode) } : {}),
      ...(action != null ? { action: String(action) } : {}),
      ...(rest as Record<string, string>),
    },
  });
};
